/**
 * Merge Container Modal — side-by-side field comparison for merging two containers.
 */
import { useState } from "react";
import { Modal, ModalOverlay, Dialog } from "@/components/application/modals/modal";
import { XClose } from "@untitledui/icons";
import { useMergeContainers } from "@/hooks/api/use-review-center";
import { toast } from "@/lib/toast-service";

interface MergeContainerModalProps {
    isOpen: boolean;
    onClose: () => void;
    sourceId: string;
    targetId: string;
    sourceLabel?: string;
    targetLabel?: string;
}

const MERGE_FIELDS = [
    { key: "booking_number", label: "Booking Number" },
    { key: "container_number", label: "Container Number" },
    { key: "bl_number", label: "BL Number" },
    { key: "status", label: "Status" },
    { key: "carrier_name", label: "Carrier" },
    { key: "vessel_name", label: "Vessel" },
    { key: "voyage_number", label: "Voyage" },
] as const;

export function MergeContainerModal({ isOpen, onClose, sourceId, targetId, sourceLabel, targetLabel }: MergeContainerModalProps) {
    // We need both containers' data — use review item detail for source (already loaded) and fetch target
    // For simplicity, use the review items query to get context
    const [fieldChoices, setFieldChoices] = useState<Record<string, "source" | "target">>({});

    const mergeMutation = useMergeContainers();

    const handleMerge = async () => {
        try {
            await mergeMutation.mutateAsync({
                source_id: sourceId,
                target_id: targetId,
                field_choices: fieldChoices,
            });
            toast.success("Containers merged successfully");
            onClose();
        } catch {
            toast.error("Failed to merge containers");
        }
    };

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl w-full">
                    {/* Header */}
                    <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-primary">Merge Containers</h3>
                        <button onClick={onClose} className="rounded-lg p-1 text-quaternary hover:bg-secondary hover:text-primary">
                            <XClose className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Explanation */}
                    <p className="mb-4 text-sm text-tertiary">
                        Select which value to keep for each field. The source container will be archived
                        and all its documents will be moved to the target.
                    </p>

                    {/* Field comparison table */}
                    <div className="overflow-hidden rounded-lg border border-secondary">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-tertiary">Field</th>
                                    <th className="px-3 py-2 text-center text-xs font-medium text-tertiary">
                                        {sourceLabel || "Source"} (source)
                                    </th>
                                    <th className="px-3 py-2 text-center text-xs font-medium text-tertiary">
                                        {targetLabel || "Target"} (target)
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {MERGE_FIELDS.map(({ key, label }) => {
                                    const choice = fieldChoices[key] || "target";
                                    return (
                                        <tr key={key}>
                                            <td className="px-3 py-2 font-medium text-secondary-foreground">{label}</td>
                                            <td className="px-3 py-2 text-center">
                                                <label className="inline-flex cursor-pointer items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name={key}
                                                        checked={choice === "source"}
                                                        onChange={() => setFieldChoices({ ...fieldChoices, [key]: "source" })}
                                                        className="h-4 w-4 rounded-full border-secondary text-brand-600 focus:ring-brand-500"
                                                    />
                                                    <span className="text-xs text-primary">{sourceLabel || "Source"}</span>
                                                </label>
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <label className="inline-flex cursor-pointer items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name={key}
                                                        checked={choice === "target"}
                                                        onChange={() => setFieldChoices({ ...fieldChoices, [key]: "target" })}
                                                        className="h-4 w-4 rounded-full border-secondary text-brand-600 focus:ring-brand-500"
                                                    />
                                                    <span className="text-xs text-primary">{targetLabel || "Target"}</span>
                                                </label>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <p className="mt-3 text-xs text-tertiary">
                        All documents from both containers will be merged into the target container.
                    </p>

                    {/* Actions */}
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="rounded-lg border border-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleMerge}
                            disabled={mergeMutation.isPending}
                            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                            {mergeMutation.isPending ? "Merging..." : "Merge"}
                        </button>
                    </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
